import React from 'react';
import { Shape, Line, Circle } from 'react-konva';

const CONTOUR_STROKE = '#3F51B5';
const CONTOUR_FILL = 'rgba(63, 81, 181, 0.2)';
export const MEASURE_COLOR = '#FF5722';

// Filled polygon of a figure contour.
export function ContourShape({ points }) {
  if (!points || points.length === 0) return null;

  return (
    <Shape
      sceneFunc={(context, shape) => {
        context.beginPath();
        context.moveTo(points[0][0], points[0][1]);
        points.slice(1).forEach(([x, y]) => context.lineTo(x, y));
        context.closePath();
        context.fillStrokeShape(shape);
      }}
      strokeWidth={3}
      stroke={CONTOUR_STROKE}
      fill={CONTOUR_FILL}
    />
  );
}

// Outline of the printed scale box, rounded on its long side.
export function ScaleRect({ figure }) {
  const w = figure.x2 - figure.x1;
  const h = figure.y2 - figure.y1;
  const r = Math.min(w / 2, 10);

  return (
    <Shape
      sceneFunc={(context, shape) => {
        context.beginPath();
        if (Math.min(w, h) > 20) roundedScalePath(context, figure, r);
        else context.rect(figure.x1, figure.y1, w, h);
        context.closePath();
        context.fillStrokeShape(shape);
      }}
      stroke="black"
      strokeWidth={0.2}
      fill="transparent"
    />
  );
}

// Traces the scale outline with semicircular caps on its long side.
function roundedScalePath(context, { x1, y1, x2, y2 }, r) {
  const path =
    x2 - x1 > y2 - y1
      ? [
          [x1 + r, y1],
          [x2, y1, x2, y1 + r],
          [x2, y2 - r],
          [x2, y2, x2 - r, y2],
          [x1 + r, y2],
          [x1, y2, x1, y2 - r],
          [x1, y1 + r],
          [x1, y1, x1 + r, y1]
        ]
      : [
          [x1, y1 + r],
          [x1, y1, x1 + r, y1],
          [x2 - r, y1],
          [x2, y1, x2, y1 + r],
          [x2, y2 - r],
          [x2, y2, x2 - r, y2],
          [x1 + r, y2],
          [x1, y2, x1, y2 - r]
        ];

  context.moveTo(...path[0]);
  path
    .slice(1)
    .forEach((point) => (point.length === 2 ? context.lineTo(...point) : context.arcTo(...point)));
}

export function MeasureLine({ start, end, scale, dashed }) {
  return (
    <Line
      points={[start.x, start.y, end.x, end.y]}
      stroke={MEASURE_COLOR}
      strokeWidth={2 / scale}
      dash={dashed ? [5 / scale, 5 / scale] : undefined}
    />
  );
}

// Draggable end point of the scale measure line.
export function MeasureHandle({ point, scale, onDrag }) {
  return (
    <Circle
      x={point.x}
      y={point.y}
      radius={6 / scale}
      fill={MEASURE_COLOR}
      stroke="#fff"
      strokeWidth={2 / scale}
      draggable
      onDragMove={onDrag}
    />
  );
}

// Measurement line while drawing a scale, with handles once it is complete.
export function MeasureOverlay({ measure, scale, onDragHandle }) {
  if (!measure.done) {
    return <MeasureLine start={measure.start} end={measure.end} scale={scale} dashed />;
  }

  return (
    <>
      <MeasureLine start={measure.start} end={measure.end} scale={scale} />
      <MeasureHandle point={measure.start} scale={scale} onDrag={(e) => onDragHandle('start', e)} />
      <MeasureHandle point={measure.end} scale={scale} onDrag={(e) => onDragHandle('end', e)} />
    </>
  );
}
